import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Cropper } from 'react-image-cropper';

import { Button, Modal, Typography } from 'antd';

import clsx from 'clsx';

import imageCompress from './imagerCompress';
import ImageReader from './ImageReader';
import type { IImageReaderResult } from './ImageReader';

interface IProps {
  opened: boolean;
  width: number;
  height: number;
  onComplete: (image: string) => void;
  onClose: () => void;
}

type Dimensions = { width: number; height: number };

const RESIZE_DEBOUNCE_MS = 500;
const DIALOG_WIDTH_OFFSET = 90;
const DIALOG_HEIGHT_OFFSET = 180;

const calculateRegion = (imgWidth: number, imgHeight: number): Dimensions => {
  const dialogWidth = window.innerWidth - DIALOG_WIDTH_OFFSET;
  const dialogHeight = window.innerHeight - DIALOG_HEIGHT_OFFSET;

  if (imgHeight <= dialogHeight && imgWidth <= dialogWidth) {
    return { width: Math.floor(imgWidth), height: Math.floor(imgHeight) };
  }

  if (imgWidth > dialogWidth) {
    const widthRatio = dialogWidth / imgWidth;
    return calculateRegion(dialogWidth, imgHeight * widthRatio);
  }

  const heightRatio = dialogHeight / imgHeight;
  return calculateRegion(imgWidth * heightRatio, dialogHeight);
};

const ImageSelector = ({ opened, width, height, onComplete, onClose }: IProps) => {
  const [image, setImage] = useState<IImageReaderResult | null>(null);
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);
  const cropperRef = useRef<any>(null);
  const resizeTimeoutRef = useRef<any>(null);
  const imageRef = useRef(image);
  imageRef.current = image;
  const dimensionsRef = useRef(dimensions);
  dimensionsRef.current = dimensions;

  const reCalculateRegion = useCallback(() => {
    clearTimeout(resizeTimeoutRef.current);
    resizeTimeoutRef.current = setTimeout(() => {
      const currentImage = imageRef.current;
      const currentDimensions = dimensionsRef.current;

      if (!currentImage) {
        return;
      }

      const newDimensions = calculateRegion(currentImage.width, currentImage.height);

      if (newDimensions.width === currentDimensions?.width && newDimensions.height === currentDimensions?.height) {
        return;
      }

      setImage(null);
      setTimeout(() => {
        setImage(currentImage);
        setDimensions(newDimensions);
      }, 0);
    }, RESIZE_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', reCalculateRegion);

    return () => {
      window.removeEventListener('resize', reCalculateRegion);
      clearTimeout(resizeTimeoutRef.current);
    };
  }, [reCalculateRegion]);

  const handleAfterClose = useCallback(() => {
    setImage(null);
    setDimensions(null);
  }, []);

  const handleSave = useCallback(async () => {
    const result = await imageCompress(cropperRef.current.crop(), width, height);
    onComplete(result);
  }, [width, height, onComplete]);

  const handleSetImage = useCallback((newImage: IImageReaderResult) => {
    const newDimensions = calculateRegion(newImage.width, newImage.height);
    setImage(newImage);
    setDimensions(newDimensions);
  }, []);

  const getCropperRef = useCallback((ref: any) => {
    cropperRef.current = ref;
  }, []);

  return (
    <Modal
      open={opened}
      width={{ xs: 'calc(100vw - 50px)', lg: '800px' }}
      centered
      keyboard={false}
      maskClosable={false}
      afterClose={handleAfterClose}
      onCancel={onClose}
      title='Selecionar Imagem'
      footer={
        <div className='flex justify-end gap-2'>
          {!!image && <ImageReader onLoad={handleSetImage} />}
          <Button onClick={onClose}>Cancelar</Button>
          <Button type='primary' disabled={!image} onClick={handleSave} id='upload-image-button-confirm'>
            OK
          </Button>
        </div>
      }
    >
      <div>
        <Typography.Text className='block text-center'>
          <strong>Tamanho recomendado:</strong> {height}px de altura x {width}px de largura.
        </Typography.Text>
      </div>
      <div
        className={clsx(
          'mt-0 mb-0 max-h-[calc(100vh-140px)] overflow-auto rounded-sm border-2 border-dashed border-[#ccc] bg-[#f9f9f9]',
          {
            'p-6': !image
          }
        )}
      >
        {!image && <ImageReader onLoad={handleSetImage} droppable />}

        {image && dimensions && (
          <div className='mx-auto shadow-[5px_5px_10px_#00000040] [&_img]:max-w-none!' style={dimensions}>
            <Cropper
              src={image.url}
              ratio={width / height}
              width={width}
              height={height}
              allowNewSelection={false}
              ref={getCropperRef}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImageSelector;
